/**
 * AzureConfigModal Component
 * Modal for adding/editing Azure AI Foundry configurations
 * EXACT mockup implementation
 */

import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../redux";
import {
  createOrUpdateConfig,
  fetchAllConfigs,
  testConnection,
  clearTestResult,
} from "../../redux/slices/llmConfigsSlice";
import {
  LLMConfiguration,
  LLMConfigCreate,
  LLMConfigUpdate,
} from "../../types/llm.types";
import llmConfigsApi from "../../api/llmConfigsApi";
import { X, Eye, EyeOff, CheckCircle2, XCircle } from "lucide-react";

interface InputFieldProps {
  label: string;
  placeholder: string;
  helperText: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  showPasswordToggle?: boolean;
}

const InputField = ({
  label,
  placeholder,
  helperText,
  value,
  onChange,
  type = "text",
  showPasswordToggle = false,
}: InputFieldProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const inputType = showPasswordToggle
    ? showPassword
      ? "text"
      : "password"
    : type;

  return (
    <div className="flex flex-col gap-2 mb-5">
      <label className="text-[14px] font-medium text-slate-200">{label}</label>
      <div className="relative">
        <input
          type={inputType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-[#1a2235]/50 border border-slate-700/50 rounded-lg px-4 py-2.5 text-[14px] text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-blue-500/50 transition-colors"
        />
        {showPasswordToggle && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>
      <p className="text-[12px] text-slate-500">{helperText}</p>
    </div>
  );
};

interface AzureConfigModalProps {
  config?: LLMConfiguration;
  onClose: () => void;
  onSave: () => void;
}

export const AzureConfigModal: React.FC<AzureConfigModalProps> = ({
  config,
  onClose,
  onSave,
}) => {
  const dispatch = useAppDispatch();
  const { saving, testing, testResult } = useAppSelector(
    (state) => state.llmConfigs
  );

  // Form state
  const [configName, setConfigName] = useState(config?.config_name || "");
  const [customEndpoint, setCustomEndpoint] = useState(
    config?.custom_endpoint || ""
  );
  const [apiKey, setApiKey] = useState("");
  const [deploymentName, setDeploymentName] = useState(
    config?.deployment_name || ""
  );
  const [modelName, setModelName] = useState(config?.model_name || "");
  const [isDefault, setIsDefault] = useState(config?.is_default || false);

  useEffect(() => {
    return () => {
      dispatch(clearTestResult());
    };
  }, [dispatch]);

  const handleSave = async () => {
    if (config?.id) {
      // Update existing
      const updateData: LLMConfigUpdate = {
        model_name: modelName || deploymentName,
        is_default: isDefault,
        custom_endpoint: customEndpoint,
        deployment_name: deploymentName,
        config_name: configName,
      };

      if (apiKey) {
        updateData.api_key = apiKey;
      }

      try {
        await llmConfigsApi.updateConfigById(config.id, updateData);
        dispatch(fetchAllConfigs());
        onSave();
        onClose();
      } catch (error) {
        console.error("Failed to update config:", error);
      }
    } else {
      // Create new
      const configData: LLMConfigCreate = {
        provider: "azure_openai",
        api_key: apiKey,
        model_name: modelName || deploymentName,
        is_default: isDefault,
        custom_endpoint: customEndpoint,
        deployment_name: deploymentName,
        config_name: configName,
      };

      const result = await dispatch(createOrUpdateConfig(configData));

      if (createOrUpdateConfig.fulfilled.match(result)) {
        onSave();
        onClose();
      }
    }
  };

  const handleTestConnection = async () => {
    await dispatch(
      testConnection({
        provider: "azure_openai",
        api_key: apiKey,
        model_name: modelName || deploymentName,
        custom_endpoint: customEndpoint,
        deployment_name: deploymentName,
      })
    );
  };

  const isFormValid = () => {
    return configName && customEndpoint && apiKey && deploymentName;
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="w-full max-w-[540px] bg-[#111827] border border-slate-800 rounded-2xl shadow-2xl overflow-hidden mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-slate-800/50">
          <h2 className="text-[18px] font-semibold text-white">
            {config ? "Edit" : "Add"} Azure AI Foundry Configuration
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-4">
          <InputField
            label="Configuration Name"
            placeholder="e.g., GPT-4 Production"
            helperText="Friendly name for this deployment"
            value={configName}
            onChange={setConfigName}
          />

          <InputField
            label="Azure Endpoint URL"
            placeholder="https://your-resource.services.ai.azure.com"
            helperText="Your Azure AI Foundry endpoint URL"
            value={customEndpoint}
            onChange={setCustomEndpoint}
          />

          <InputField
            label="API Key"
            placeholder={config ? "••••••••••••" : "Enter your API key"}
            helperText="Found in Azure Portal > Keys"
            value={apiKey}
            onChange={setApiKey}
            showPasswordToggle={true}
          />

          <InputField
            label="Deployment/Model Name"
            placeholder="e.g., gpt-4o, grok-2"
            helperText="The model deployed in your Azure resource"
            value={deploymentName}
            onChange={setDeploymentName}
          />

          {/* Set as Default Checkbox */}
          <div className="flex items-center gap-3 mt-1">
            <div
              className={`w-5 h-5 rounded flex items-center justify-center cursor-pointer transition-colors ${
                isDefault ? "bg-blue-500" : "bg-slate-700"
              }`}
              onClick={() => setIsDefault(!isDefault)}
            >
              {isDefault && (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              )}
            </div>
            <span
              className="text-[14px] text-slate-200 cursor-pointer"
              onClick={() => setIsDefault(!isDefault)}
            >
              Set as default
            </span>
          </div>

          {/* Test Result */}
          {testResult && (
            <div
              className={`flex items-center gap-2 p-3 rounded-lg text-sm mb-4 ${
                testResult.success
                  ? "bg-green-500/10 text-green-400 border border-green-500/20"
                  : "bg-red-500/10 text-red-400 border border-red-500/20"
              }`}
            >
              {testResult.success ? (
                <CheckCircle2 size={16} />
              ) : (
                <XCircle size={16} />
              )}
              <span>{testResult.message}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-[#161e2e] border-t border-slate-800/50 flex items-center justify-end">
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={!isFormValid() || saving}
              className="px-5 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-blue-400 text-white text-[14px] font-semibold shadow-lg shadow-blue-500/20 hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving
                ? "Saving..."
                : config
                ? "Update Configuration"
                : "Add Configuration"}
            </button>
            <button
              onClick={onClose}
              className="px-5 py-2 rounded-lg bg-slate-800/50 text-slate-300 text-[14px] font-medium hover:bg-slate-800 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
